'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { AircraftModel } from '@/models/aircraft-model';
import { AircraftType } from '@/models/aircraft';
import { createAircraftModelDoc } from '@/lib/aircraft-models';

interface CreateAircraftModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (model: AircraftModel) => void;
  initialName?: string;
}

export function CreateAircraftModelDialog({
  open,
  onOpenChange,
  onCreated,
  initialName = '',
}: CreateAircraftModelDialogProps) {
  const firestore = useFirestore();
  const auth = useAuth();
  const { toast } = useToast();

  const [name, setName] = React.useState(initialName);
  const [type, setType] = React.useState<AircraftType>('Multi-Rotor');
  const [manufacturer, setManufacturer] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setType('Multi-Rotor');
      setManufacturer('');
    }
  }, [open, initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firestore || !auth.currentUser) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'You must be logged in to create an aircraft model',
      });
      return;
    }

    if (!name.trim()) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Name is required',
      });
      return;
    }

    setSaving(true);
    try {
      const newModel = await createAircraftModelDoc(firestore, {
        name: name.trim(),
        type,
        manufacturer: manufacturer.trim() || undefined,
        createdBy: auth.currentUser.uid,
      });

      toast({
        title: 'Aircraft Model Created',
        description: `"${newModel.name}" has been added`,
      });

      onCreated(newModel);
    } catch (error) {
      console.error('Failed to create aircraft model:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create aircraft model',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Aircraft Model</DialogTitle>
          <DialogDescription>
            Add a new aircraft model that can be used to tag photos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., DJI Mavic 3 Pro"
              disabled={saving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">
              Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as AircraftType)}
              disabled={saving}
            >
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Multi-Rotor">Multi-Rotor</SelectItem>
                <SelectItem value="Fixed Wing">Fixed Wing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="manufacturer">Manufacturer (Optional)</Label>
            <Input
              id="manufacturer"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              placeholder="e.g., DJI"
              disabled={saving}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Model
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
